import { CodaltComponent } from '../codalt/codalt.component';
import { UserService } from './../../services/user.service';
import { Component, signal, WritableSignal, computed } from '@angular/core';
import { UserInsight } from '../../classes/user-insight';
import { Routenames } from '../../routenames';

interface TableData {
    userName: string;
    userId: number;
    weekCounts: { [weekLabel: string]: number };
}

@Component({
    selector: 'app-user-insight',
    templateUrl: './user-insight.component.html',
    styleUrl: './user-insight.component.scss',
})
export class UserInsightComponent extends CodaltComponent {
    override Routenames = Routenames;
    data: WritableSignal<UserInsight[]> = signal([]);
    numberOfWeeks: WritableSignal<number> = signal(12); // Default to 2 weeks
    startDate = signal('');
    endDate = signal('');

    // Computed properties for the table
    uniqueWeeks = computed(() => {
        if (!this.startDate() || !this.endDate()) {
            // Fallback to weeks from data if no date range available
            const weeks = this.data().map((item) => item.week_label);
            return [...new Set(weeks)].sort((a, b) => {
                const aMatch = a.match(/Week (\d+), (\d+)/);
                const bMatch = b.match(/Week (\d+), (\d+)/);
                if (aMatch && bMatch) {
                    const aYear = parseInt(aMatch[2]);
                    const bYear = parseInt(bMatch[2]);
                    const aWeek = parseInt(aMatch[1]);
                    const bWeek = parseInt(bMatch[1]);

                    if (aYear !== bYear) return aYear - bYear;
                    return aWeek - bWeek;
                }
                return a.localeCompare(b);
            });
        }

        // Generate all weeks in the date range
        const weeks: string[] = [];
        const start = new Date(this.startDate());
        const end = new Date(this.endDate());

        // Find the start of the week for the start date (Monday)
        const startOfWeek = new Date(start);
        const dayOfWeek = startOfWeek.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so subtract 6; otherwise subtract (dayOfWeek - 1)
        startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);

        // Generate weeks
        for (let weekStart = new Date(startOfWeek); weekStart <= end; weekStart.setDate(weekStart.getDate() + 7)) {
            const year = weekStart.getFullYear();
            const weekNumber = this.getWeekNumber(weekStart);
            weeks.push(weekNumber.toString());
        }

        return weeks;
    });

    tableData = computed(() => {
        const userMap = new Map<string, TableData>();

        this.data().forEach((item) => {
            const key = `${item.user_name}_${item.user_id}`;
            if (!userMap.has(key)) {
                userMap.set(key, {
                    userName: item.user_name,
                    userId: item.user_id,
                    weekCounts: {},
                });
            }
            userMap.get(key)!.weekCounts[item.week_number] = item.count;
        });

        return Array.from(userMap.values()).sort((a, b) => {
            const totalA = Object.values(a.weekCounts).reduce((sum, count) => sum + count, 0);
            const totalB = Object.values(b.weekCounts).reduce((sum, count) => sum + count, 0);
            return totalB - totalA; // Sort descending (highest first)
        });
    });
    maxCount = computed(() => {
        return Math.max(...this.data().map((item) => item.count), 1);
    });

    constructor(private userService: UserService) {
        super();
        this.loadData();
    }

    loadData() {
        this.subscriptions.add(
            this.userService
                .getUserInsight(this.numberOfWeeks())
                .pipe()
                .subscribe((response: any) => {
                    if (response.success && response.data) {
                        this.data.set(response.data.data);
                        this.startDate.set(response.data.start_date);
                        this.endDate.set(response.data.end_date);
                    }
                })
        );
    }

    onWeeksChange(weeks: number) {
        this.numberOfWeeks.set(weeks);
        this.loadData();
    }

    getCountForUserAndWeek(user: TableData, weekLabel: string): number {
        return user.weekCounts[weekLabel] || 0;
    }

    getTotalForUser(user: TableData): number {
        return Object.values(user.weekCounts).reduce((sum, count) => sum + count, 0);
    }

    getDisplayWeekNumber(weekLabel: string): string {
        // Extract just the week number from "Week X, YYYY" format for display
        const match = weekLabel.match(/Week (\d+)/);
        return match ? match[1] : weekLabel;
    }

    getCellBackgroundColor(count: number): string {
        // Use the same color for 0 and 1-15 calls (lightest color)
        if (count <= 15) {
            return '#FFEF9C'; // Light yellow for 0-15 calls
        }

        // Scale between 16 and 100+ calls
        const intensity = Math.min((count - 15) / 85, 1); // 85 = 100 - 15

        // Start color: #FFEF9C (255, 239, 156)
        // End color: #63BE7B (99, 190, 123)
        const startR = 255,
            startG = 239,
            startB = 156;
        const endR = 99,
            endG = 190,
            endB = 123;

        const red = Math.round(startR + (endR - startR) * intensity);
        const green = Math.round(startG + (endG - startG) * intensity);
        const blue = Math.round(startB + (endB - startB) * intensity);

        return `rgb(${red}, ${green}, ${blue})`;
    }
    private getWeekNumber(date: Date): number {
        // ISO week number calculation
        const target = new Date(date.valueOf());
        const dayNr = (date.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
        }
        return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
    }
}
